import type { TerraApiRouteResponses } from '@terra/shared-utils';
import {
  createApiHeaders,
  tenantAwareApiWrapper,
  withOrchestratorClient,
  withServiceClient,
} from './portal-http';

import { endpointsClient } from '@terra/shared-rest-clients-endpoints';
import { assetsClient } from '@terra/shared-rest-clients-assets';
import { gitClient } from '@terra/shared-rest-clients-git';
import type { z } from 'zod';
import type { EndpointsContract } from '@terra/shared-contracts-endpoints';
import type {
  EndpointExpectedResponseObservationsResponseSchema,
  ExpectedResponseObservationFilterOptionsSchema,
  ExpectedResponseObservationsQuerySchema,
} from '@terra/shared-contracts-endpoints';
import type {
  EndpointsPreviewQueryDto,
  EndpointsPreviewReturnDto,
} from '@terra/shared-contracts-endpoints';
import type { AssetsContract } from '@terra/shared-contracts-assets';
import type {
  ApplicationEntityCountsQueryDto,
  ApplicationEntityCountsResponseDto,
  AssetsQueryDto,
  AssetsPreviewReturnDto,
  UnverifiedAssetDetailDto,
} from '@terra/shared-contracts-assets';
import type { gitContract } from '@terra/shared-contracts-git';
import type { DomainTreeResponseDto } from '@terra/shared-contracts-assets';

type ExpectedResponseObservationsQueryDto = z.infer<
  typeof ExpectedResponseObservationsQuerySchema
>;
type EndpointExpectedResponseObservationsResponseDto = z.infer<
  typeof EndpointExpectedResponseObservationsResponseSchema
>;
type ExpectedResponseObservationFilterOptionsDto = z.infer<
  typeof ExpectedResponseObservationFilterOptionsSchema
>;

export const portalAssetsApi = {
  getEndpointsByApplication: (applicationId: string) =>
    tenantAwareApiWrapper<typeof EndpointsContract.getEndpoints>({
      endpointFn: (headers) => {
        return endpointsClient.getEndpoints({
          params: { applicationId },
          ...withOrchestratorClient(headers),
        });
      },
    }),

  getAllEndpoints: () =>
    tenantAwareApiWrapper<typeof EndpointsContract.getAllEndpoints>({
      endpointFn: (headers) => {
        return endpointsClient.getAllEndpoints({
          ...withOrchestratorClient(headers),
        });
      },
    }),

  getEndpointsPreview: async (
    applicationId: string,
    options?: EndpointsPreviewQueryDto,
  ): Promise<EndpointsPreviewReturnDto | undefined> =>
    tenantAwareApiWrapper<typeof EndpointsContract.getEndpointsPreview>({
      endpointFn: async (headers) => {
        const response = await endpointsClient.getEndpointsPreview({
          params: { applicationId },
          query: options,
          ...withOrchestratorClient(headers),
        });

        return response;
      },
    }),

  getExpectedResponseObservationsByNeoRun: async (
    applicationId: string,
    threadId: string,
    query?: ExpectedResponseObservationsQueryDto,
  ): Promise<EndpointExpectedResponseObservationsResponseDto | undefined> =>
    tenantAwareApiWrapper<
      typeof EndpointsContract.getExpectedResponseObservationsByNeoRun
    >({
      endpointFn: async (headers) =>
        endpointsClient.getExpectedResponseObservationsByNeoRun({
          params: { applicationId, threadId },
          query: { isStateChanging: undefined, ...(query ?? {}) },
          ...withOrchestratorClient(headers),
        }),
    }),

  getExpectedResponseObservationFiltersByNeoRun: async (
    applicationId: string,
    threadId: string,
  ): Promise<ExpectedResponseObservationFilterOptionsDto | undefined> =>
    tenantAwareApiWrapper<
      typeof EndpointsContract.getExpectedResponseObservationFiltersByNeoRun
    >({
      endpointFn: async (headers) =>
        endpointsClient.getExpectedResponseObservationFiltersByNeoRun({
          params: { applicationId, threadId },
          ...withOrchestratorClient(headers),
        }),
    }),

  searchAssetsPreview: async (
    applicationId: string,
    options?: AssetsQueryDto,
  ): Promise<AssetsPreviewReturnDto | undefined> =>
    tenantAwareApiWrapper<typeof AssetsContract.searchAssetsPreview>({
      endpointFn: (headers) =>
        assetsClient.searchAssetsPreview({
          params: { applicationId },
          body: options ?? {},
          ...withOrchestratorClient(headers),
        }),
    }),

  getUnverifiedAsset: async ({
    applicationId,
    candidateId,
  }: {
    applicationId: string;
    candidateId: string;
  }): Promise<UnverifiedAssetDetailDto | undefined> =>
    tenantAwareApiWrapper<typeof AssetsContract.getUnverifiedAsset>({
      endpointFn: (headers) =>
        assetsClient.getUnverifiedAsset({
          params: { applicationId, candidateId },
          ...withOrchestratorClient(headers),
        }),
    }),

  deleteUnverifiedAsset: async ({
    applicationId,
    candidateId,
  }: {
    applicationId: string;
    candidateId: string;
  }) =>
    tenantAwareApiWrapper<typeof AssetsContract.deleteUnverifiedAsset>({
      endpointFn: (headers) =>
        assetsClient.deleteUnverifiedAsset({
          params: { applicationId, candidateId },
          ...withOrchestratorClient(headers),
        }),
    }),

  rerunUnverifiedAsset: async ({
    applicationId,
    candidateId,
    context,
  }: {
    applicationId: string;
    candidateId: string;
    context?: {
      instructions?: string;
      parameters?: string;
      harData?: string;
      usageExample?: string;
    };
  }) =>
    tenantAwareApiWrapper<typeof AssetsContract.rerunUnverifiedAsset>({
      endpointFn: (headers) =>
        assetsClient.rerunUnverifiedAsset({
          params: { applicationId, candidateId },
          body: context ?? {},
          ...withOrchestratorClient(headers),
        }),
    }),

  getUnverifiedAssetProbeStatus: async ({
    applicationId,
    candidateId,
  }: {
    applicationId: string;
    candidateId: string;
  }) =>
    tenantAwareApiWrapper<typeof AssetsContract.getUnverifiedAssetProbeStatus>({
      endpointFn: (headers) =>
        assetsClient.getUnverifiedAssetProbeStatus({
          params: { applicationId, candidateId },
          ...withOrchestratorClient(headers),
        }),
    }),

  countApplicationEntities: async ({
    applicationId,
    kind,
    mode,
    lifecycleState,
  }: {
    applicationId: string;
  } & ApplicationEntityCountsQueryDto): Promise<
    ApplicationEntityCountsResponseDto | undefined
  > =>
    tenantAwareApiWrapper<typeof AssetsContract.countApplicationEntities>({
      endpointFn: async (headers) => {
        const response = await assetsClient.countApplicationEntities({
          params: { applicationId },
          query: {
            kind,
            mode,
            lifecycleState,
          },
          ...withOrchestratorClient(headers),
        });

        return response;
      },
    }),

  getDomainTree: async ({
    applicationId,
    includeMethodNodes = true,
    applyApplicationLevelRoot = false,
    includeEndpoints = true,
    includeWebPages = true,
    type,
  }: {
    applicationId: string;
    includeMethodNodes?: boolean;
    applyApplicationLevelRoot?: boolean;
    includeEndpoints?: boolean;
    includeWebPages?: boolean;
    type?: 'endpoints' | 'agents' | 'webpages' | 'ALL';
  }): Promise<DomainTreeResponseDto> =>
    tenantAwareApiWrapper<typeof AssetsContract.getDomainTree>({
      endpointFn: async (headers) => {
        const response = await assetsClient.getDomainTree({
          params: { applicationId },
          query: {
            includeMethodNodes,
            applyApplicationLevelRoot,
            includeEndpoints,
            includeWebPages,
            type,
          },
          ...withOrchestratorClient(headers),
        });

        return response;
      },
    }),

  getEndpointById: (endpointId: string) =>
    tenantAwareApiWrapper<typeof EndpointsContract.getEndpointById>({
      endpointFn: (headers) => {
        return endpointsClient.getEndpointById({
          params: { id: endpointId },
          ...withOrchestratorClient(headers),
        });
      },
    }),

  deleteEndpoint: (endpointId: string) =>
    tenantAwareApiWrapper<typeof EndpointsContract.deleteEndpoint>({
      endpointFn: (headers) =>
        endpointsClient.deleteEndpoint({
          params: { id: endpointId },
          ...withOrchestratorClient(headers),
        }),
    }),

  getHarEntriesByEndpoint: async (
    endpointId: string,
    options: { skip?: number; limit?: number } = {},
  ) => {
    try {
      const headers = await createApiHeaders();

      const response = await endpointsClient.getHarEntriesByEndpoint({
        params: { id: endpointId },
        query: {
          skip: options.skip || 0,
          limit: options.limit || 50,
        },
        ...withOrchestratorClient(headers),
      });

      return response;
    } catch (error) {
      console.error('Failed to get HAR entries by endpoint:', error);

      throw error;
    }
  },

  getHarEntriesByApplication: async (
    applicationId: string,
    options: { skip?: number; limit?: number } = {},
  ) => {
    try {
      const headers = await createApiHeaders();

      const response = await endpointsClient.getHarEntriesByApplication({
        params: { applicationId },
        query: {
          skip: options.skip || 0,
          limit: options.limit || 50,
        },
        ...withOrchestratorClient(headers),
      });

      return response;
    } catch (error) {
      console.error('Failed to get HAR entries by application:', error);

      throw error;
    }
  },

  getHarEntryById: ({
    applicationId,
    harEntryId,
  }: {
    applicationId: string;
    harEntryId: string;
  }) =>
    tenantAwareApiWrapper<typeof EndpointsContract.getHarEntryById>({
      endpointFn: (headers) =>
        endpointsClient.getHarEntryById({
          params: { applicationId, harEntryId },
          ...withOrchestratorClient(headers),
        }),
    }),

  // Git service endpoints — oRPC client

  getEndpointFiles: (applicationId: string, endpointId: string) =>
    tenantAwareApiWrapper<typeof gitContract.getEndpointFiles>({
      endpointFn: (headers) =>
        gitClient.getEndpointFiles({
          params: { applicationId, endpointId },
          ...withServiceClient(headers, 'git'),
        }) as Promise<
          TerraApiRouteResponses<typeof gitContract.getEndpointFiles>
        >,
    }),

  getAllEndpointFiles: (applicationId: string) =>
    tenantAwareApiWrapper<typeof gitContract.getAllApplicationFiles>({
      endpointFn: (headers) =>
        gitClient.getAllApplicationFiles({
          params: { applicationId },
          ...withServiceClient(headers, 'git'),
        }) as Promise<
          TerraApiRouteResponses<typeof gitContract.getAllApplicationFiles>
        >,
    }),

  getFileContent: (applicationId: string, fileId: string) =>
    tenantAwareApiWrapper<typeof gitContract.getFileContent>({
      endpointFn: (headers) => {
        return gitClient.getFileContent({
          params: { applicationId, fileId },
          ...withServiceClient(headers, 'git'),
        });
      },
      useBodyMessage: true,
    }),

  getEndpointCodeGraph: (applicationId: string, endpointId: string) =>
    tenantAwareApiWrapper<typeof gitContract.getEndpointCodeGraph>({
      endpointFn: (headers) => {
        return gitClient.getEndpointCodeGraph({
          params: { applicationId, endpointId },
          ...withServiceClient(headers, 'git'),
        });
      },
    }),

  getCodeGraphById: (graphId: string) =>
    tenantAwareApiWrapper<typeof gitContract.getCodeGraphById>({
      endpointFn: (headers) => {
        return gitClient.getCodeGraphById({
          params: { graphId },
          ...withServiceClient(headers, 'git'),
        });
      },
    }),

  getApplicationEntryPoints: (
    applicationId: string,
    filters?: { entryPointId?: string; endpointId?: string },
  ) =>
    tenantAwareApiWrapper<typeof gitContract.getApplicationEntryPoints>({
      endpointFn: (headers) => {
        return gitClient.getApplicationEntryPoints({
          params: { applicationId },
          query: filters || {},
          ...withServiceClient(headers, 'git'),
        });
      },
    }),

  // Signal actions
};
